import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { SUITES, NAV } from "../data/content";
import Seo from "../components/Seo";
import SuiteGallery from "../components/SuiteGallery";
import Breadcrumbs from "../components/Breadcrumbs";
import Img from "../components/Img";

// Trim a string to a clean meta-description length (<=160) at a word boundary.
const metaClamp = (s, max = 158) => {
  if (!s || s.length <= max) return s;
  const cut = s.slice(0, max);
  const sp = cut.lastIndexOf(" ");
  return (sp > 0 ? cut.slice(0, sp) : cut).replace(/[\s—,;:.]+$/, "") + "…";
};

const SuiteDetail = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const suite = SUITES[slug];
  if (!suite) return <Navigate to="/sejours" replace />;

  const origin = "https://plumesuites.fr";
  const sizeVal = parseInt(String(suite.stats.size), 10) || undefined;
  const roomLd = {
    "@context": "https://schema.org",
    "@type": "HotelRoom",
    "@id": `${origin}/sejours/${slug}#room`,
    name: suite.name[lang],
    description: suite.intro[lang],
    image: suite.cover.startsWith("http") ? suite.cover : `${origin}${suite.cover}`,
    url: `${origin}/sejours/${slug}`,
    occupancy: { "@type": "QuantitativeValue", maxValue: suite.stats.guests, unitText: "person" },
    bed: { "@type": "BedDetails", typeOfBed: slug === "envolee" ? "King" : "Double", numberOfBeds: 1 },
    ...(sizeVal ? { floorSize: { "@type": "QuantitativeValue", value: sizeVal, unitCode: "MTK" } } : {}),
    amenityFeature: suite.details.map((d) => ({
      "@type": "LocationFeatureSpecification",
      name: d.fr,
      value: true,
    })),
    containedInPlace: { "@type": "LodgingBusiness", "@id": `${origin}/#lodging`, name: "Plume Suites" },
    ...(suite.priceFrom
      ? {
          offers: {
            "@type": "Offer",
            priceCurrency: "EUR",
            price: suite.priceFrom,
            availability: "https://schema.org/InStock",
            url: `${origin}/reserver`,
            priceSpecification: {
              "@type": "UnitPriceSpecification",
              price: suite.priceFrom,
              priceCurrency: "EUR",
              unitText: "nuit",
              referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitCode: "DAY" },
            },
          },
        }
      : {}),
  };

  return (
    <div className="bg-cream text-ink" data-testid={`suite-page-${slug}`}>
      <Seo
        title={suite.name[lang]}
        description={metaClamp(suite.intro[lang])}
        image={suite.cover}
        path={`/sejours/${slug}`}
        type="article"
        jsonLd={roomLd}
      />
      {/* HERO */}
      <section className="relative h-screen w-full overflow-hidden">
        <Img src={suite.cover} alt={t(suite.name)} priority className="absolute inset-0 w-full h-full object-cover kenburns" />
        <div className="absolute inset-0 bg-ink/35" />
        <div className="absolute inset-0 px-6 md:px-12 pt-32 pb-16 flex flex-col justify-end text-cream">
          <p className="ps-overline opacity-90 mb-4">— Plume Suites Collection</p>
          <h1 className="font-serif italic text-6xl md:text-[10rem] leading-[0.9] font-light max-w-5xl">
            {t(suite.name)}
          </h1>
        </div>
      </section>

      <Breadcrumbs
        className="pt-10 md:pt-12"
        items={[
          { name: t({ fr: "Accueil", en: "Home" }), to: "/" },
          { name: t(NAV.sejours), to: "/sejours" },
          { name: t(suite.name), to: `/sejours/${slug}` },
        ]}
      />

      {/* INTRO + KEY STATS */}
      <section className="py-24 md:py-32 px-6 md:px-12" data-testid={`suite-stats-${slug}`}>
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <p className="ps-overline text-ink/60 mb-6">— {t({ fr: "Introduction", en: "Introduction" })}</p>
            <p className="font-serif italic text-3xl md:text-5xl leading-[1.15] font-light">
              {t(suite.intro)}
            </p>
          </div>
          <div className="md:col-span-5 md:pt-12">
            <p className="ps-overline text-ink/60 mb-6">— {t({ fr: "En un coup d'œil", en: "At a glance" })}</p>
            <div className="grid grid-cols-3 gap-6 md:gap-8">
              <div>
                <div className="font-serif italic text-5xl md:text-6xl font-light leading-none">{suite.stats.rooms}</div>
                <p className="ps-overline text-ink/60 mt-3">{t(suite.stats.labels.rooms)}</p>
              </div>
              <div>
                <div className="font-serif italic text-5xl md:text-6xl font-light leading-none">{suite.stats.guests}</div>
                <p className="ps-overline text-ink/60 mt-3">{t(suite.stats.labels.guests)}</p>
              </div>
              <div>
                <div className="font-serif italic text-2xl md:text-3xl font-light leading-none mt-3">{suite.stats.size}</div>
                <p className="ps-overline text-ink/60 mt-3">{t(suite.stats.labels.size)}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FULLSCREEN OVERLAY */}
      <section className="relative h-[85vh] overflow-hidden" data-testid={`suite-overlay-${slug}`}>
        <Img
          src={suite.gallery[1]}
          alt={t(suite.name)}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-terracotta/55" />
        <div className="absolute inset-0 flex items-center justify-center px-6 md:px-12">
          <p className="font-serif italic text-cream text-4xl md:text-7xl text-center font-light leading-[1.05] max-w-4xl">
            {t(suite.overlayTitle)}
          </p>
        </div>
      </section>

      {/* EDITORIAL */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <p className="ps-overline text-ink/60 mb-6">{t({ fr: "Éditorial", en: "Editorial" })}</p>
            <h2 className="font-serif italic text-4xl md:text-6xl font-light leading-[1.05]">
              {t(suite.editorialTitle)}
            </h2>
          </div>
          <div className="md:col-span-6 md:pt-10">
            <p className="font-sans text-base md:text-lg leading-relaxed text-ink/85">
              {t(suite.editorial)}
            </p>
          </div>
        </div>
      </section>

      {/* GALLERY CARROUSEL — défile auto + draggable / swipeable */}
      <SuiteGallery images={suite.gallery} slug={slug} />

      {/* EXPERIENCE */}
      <section className="bg-ink text-cream py-24 md:py-32 px-6 md:px-12" data-testid={`suite-experience-${slug}`}>
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="ps-overline opacity-70 mb-4">{t({ fr: "Expérience", en: "Experience" })}</p>
            <h2 className="font-serif italic text-4xl md:text-6xl font-light leading-[1.05]">
              {t(suite.experienceTitle)}
            </h2>
          </div>
          <div className="md:col-span-7">
            <ul className="space-y-5 text-2xl md:text-3xl font-serif italic font-light">
              {suite.experience.map((e, i) => (
                <li key={i} className="border-b border-cream/15 pb-5 flex justify-between gap-4">
                  <span>{t(e)}</span>
                  <span className="font-sans text-xs uppercase tracking-[0.25em] text-cream/60 pt-3">0{i + 1}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* DETAILS */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <p className="ps-overline text-ink/60 mb-6">{t(suite.detailsTitle)}</p>
          <h2 className="font-serif italic text-4xl md:text-6xl font-light max-w-3xl leading-[1.05] mb-12">
            {t({ fr: "Une suite, pensée dans le détail.", en: "A suite, considered down to the detail." })}
          </h2>
          <ul className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-10 font-sans text-base md:text-lg text-ink/85">
            {suite.details.map((d, i) => (
              <li key={i} className="border-t border-ink/20 pt-4">
                <span className="ps-overline text-ink/50 block mb-1">· 0{i + 1}</span>
                {t(d)}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA RESERVATION */}
      <section className="bg-terracotta text-cream py-24 md:py-32 px-6 md:px-12 text-center" data-testid={`suite-cta-${slug}`}>
        <p className="ps-overline opacity-80 mb-6">{t({ fr: "Réservation", en: "Booking" })}</p>
        <h2 className="font-serif italic text-5xl md:text-7xl font-light max-w-3xl mx-auto leading-[1.05]">
          {t({ fr: "Réserver votre séjour.", en: "Book your stay." })}
        </h2>
        <div className="mt-12 flex items-center justify-center gap-6 flex-wrap">
          <Link
            to="/reserver"
            data-testid={`suite-book-${slug}`}
            className="inline-flex items-center justify-center bg-cream text-ink px-8 py-4 uppercase text-xs tracking-[0.28em] hover:bg-ink hover:text-cream transition-colors"
          >
            {t(NAV.book)}
          </Link>
          <Link
            to="/sejours"
            data-testid={`suite-back-${slug}`}
            className="ps-btn-outline-light"
          >
            {t({ fr: "L'autre suite", en: "The other suite" })}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SuiteDetail;
